/* eslint-disable @typescript-eslint/no-explicit-any */
// @ts-nocheck
/**
 * PowerMapView — Milestone 2 streets rollup with filter/sort UI and privacy banner.
 *
 * Filter/sort spec: memory-bank/plans/filter-sort-ui-plan.md §5.1 (mirrored from VotersView)
 * API contract: GET /streets with envelope=v2
 *
 * Layout:
 *   [H1: Power Map — Streets] [Tab strip: Streets | Blocks | Buildings (placeholders)]
 *   [Privacy Banner: "Rows with fewer than N voters are hidden…"]
 *   [FilterBar: search, precinct, subregion, min_score filter]
 *   [Active filter chips + Clear all]
 *   [DataTable: Street | Precinct | Voter count | Avg score | Contact rate | Expected ballots]
 *   [ResultsCount + pagination]
 *
 * URL-synced, server-side paginated (50 per page).
 */
import React, { useEffect, useState } from 'react';
import type { StreetRollupRow } from '@/types';
import { useListQuery } from '@/hooks/useListQuery';
import DataTable from '@/components/DataTable';
import {
  FilterBar,
  SearchInput,
  FilterSelect,
  FilterRange,
} from '@/components/filters';
import client from '@/api/client';

interface PrecinctOption {
  value: string;
  label: string;
}

interface SubregionOption {
  value: string;
  label: string;
}

interface PrivacyPolicy {
  streets: number;
  blocks: number;
  buildings: number;
  narrative: number;
  engagement: number;
}

/**
 * PowerMapView — server-paginated streets rollup with advanced filtering.
 */
export function PowerMapView({ onBack }: { onBack?: () => void }) {
  // ============================================================================
  // State for dropdowns (load from backend)
  // ============================================================================
  const [precinctOptions, setPrecinctOptions] = useState<PrecinctOption[]>([]);
  const [subregionOptions, setSubregionOptions] = useState<SubregionOption[]>([]);
  const [loadingOptions, setLoadingOptions] = useState(true);

  // Privacy banner state
  const [kAnonFloor, setKAnonFloor] = useState<number>(5);
  const [privacyError, setPrivacyError] = useState(false);

  // ============================================================================
  // Query hook: server-side list query with envelope=v2
  // ============================================================================
  const query = useListQuery<StreetRollupRow>({
    endpoint: '/geo/streets',
    initialParams: { envelope: 'v2' },
    pageSize: 50,
  });

  // ============================================================================
  // Load privacy policy on mount
  // ============================================================================
  useEffect(() => {
    async function loadPrivacyPolicy() {
      try {
        const res = await client.get<PrivacyPolicy>('/meta/privacy');
        setKAnonFloor(res.data.streets);
      } catch (err) {
        console.error('Failed to load privacy policy:', err);
        setPrivacyError(true);
        // Fallback to 5 if fetch fails
        setKAnonFloor(5);
      }
    }

    loadPrivacyPolicy();
  }, []);

  // ============================================================================
  // Load dropdown options on mount
  // ============================================================================
  useEffect(() => {
    async function loadOptions() {
      try {
        setLoadingOptions(true);
        const [precinctRes, subregionRes] = await Promise.all([
          client.get('/voters/by-precinct', { params: { limit: 500 } }),
          client.get('/voters/by-subregion').catch(() => ({ data: [] })),
        ]);

        // Both endpoints return bare arrays (no v2 envelope support yet).
        const precinctList: any[] = Array.isArray(precinctRes.data)
          ? precinctRes.data
          : precinctRes.data.items || [];
        const precincts: PrecinctOption[] = precinctList.map((p: any) => ({
          value: String(p.precinct_num),
          label: `P${p.precinct_num} — ${p.precinct_name || ''}`.trim(),
        }));
        setPrecinctOptions(precincts);

        const subregionList: any[] = Array.isArray(subregionRes.data)
          ? subregionRes.data
          : subregionRes.data.items || [];
        const subregions: SubregionOption[] = subregionList.map((sr: any) => ({
          value: sr.subregion,
          label: sr.subregion,
        }));
        setSubregionOptions(subregions);
      } catch (err) {
        console.error('Failed to load precinct/subregion options:', err);
        // Gracefully degrade: empty dropdowns
      } finally {
        setLoadingOptions(false);
      }
    }

    loadOptions();
  }, []);

  // ============================================================================
  // Handle filter changes
  // ============================================================================
  const handleSearchChange = (value: string) => {
    query.setSearch(value);
  };

  const handlePrecinctChange = (value: string | null) => {
    if (value) {
      query.setFilter('precinct_num', Number(value));
    } else {
      query.setFilter('precinct_num', null);
    }
  };

  const handleSubregionChange = (value: string | null) => {
    if (value) {
      query.setFilter('subregion', value);
    } else {
      query.setFilter('subregion', null);
    }
  };

  const handleMinScoreChange = (value: [number | null, number | null]) => {
    const [min] = value;
    if (min !== null) {
      query.setFilter('min_score__gte', min);
    } else {
      query.setFilter('min_score__gte', null);
    }
  };

  // ============================================================================
  // Extract current filter values for controls and chips
  // ============================================================================
  const currentFilters = query.filters as Record<string, any>;
  const currentPrecinct = currentFilters.precinct_num
    ? String(currentFilters.precinct_num)
    : null;
  const currentSubregion = currentFilters.subregion || null;
  const minScore = currentFilters.min_score__gte || null;

  // ============================================================================
  // Build active filter chips
  // ============================================================================
  const activeFilterChips: Array<{ id: string; label: string; value: string }> = [];

  if (query.search) {
    activeFilterChips.push({
      id: 'search',
      label: 'Search',
      value: query.search,
    });
  }

  if (currentPrecinct) {
    const precinctLabel =
      precinctOptions.find((p) => p.value === currentPrecinct)?.label || `P${currentPrecinct}`;
    activeFilterChips.push({
      id: 'precinct',
      label: 'Precinct',
      value: precinctLabel,
    });
  }

  if (currentSubregion) {
    activeFilterChips.push({
      id: 'subregion',
      label: 'Subregion',
      value: currentSubregion,
    });
  }

  if (minScore !== null) {
    activeFilterChips.push({
      id: 'minScore',
      label: 'Min Avg Score',
      value: `≥${minScore}`,
    });
  }

  // ============================================================================
  // Table columns definition
  // ============================================================================
  const columns = [
    {
      key: 'street_name',
      header: 'Street',
      sortable: true,
      render: (row: StreetRollupRow) => (
        <div className="text-fg-primary font-medium">{row.street_name}</div>
      ),
    },
    {
      key: 'precinct_num',
      header: 'Precinct',
      sortable: true,
      render: (row: any) =>
        row.precinct_num ? `P${row.precinct_num}` : '—',
    },
    {
      key: 'voter_count',
      header: 'Voter count',
      sortable: true,
      render: (row: StreetRollupRow) => (
        <div className="text-fg-secondary">{row.voter_count}</div>
      ),
    },
    {
      key: 'avg_score',
      header: 'Avg score',
      sortable: true,
      render: (row: StreetRollupRow) => (
        <ScoreBadge score={row.avg_score} />
      ),
    },
    {
      key: 'contact_rate',
      header: 'Contact rate',
      sortable: false,
      render: (row: any) => {
        // Contact rate not yet in StreetRollupRow schema
        return <span className="text-fg-muted">—</span>;
      },
    },
    {
      key: 'expected_ballots',
      header: 'Expected ballots',
      sortable: false,
      render: (row: any) => {
        // Expected ballots not in StreetRollupRow; would need PowerMapRow response
        return <span className="text-fg-muted">—</span>;
      },
    },
  ];

  // ============================================================================
  // Render
  // ============================================================================
  return (
    <div className="space-y-6">
      {/* Tab strip: Streets | Blocks | Buildings */}
      <div className="bg-bg-raised rounded-lg border border-border-base px-2">
        <div className="flex gap-2">
          <button
            className="px-4 py-2 text-sm font-medium text-status-success border-b-2 border-status-success min-h-11 sm:min-h-0"
          >
            Streets
          </button>
          <button
            disabled
            className="px-4 py-2 text-sm font-medium text-fg-muted opacity-50 cursor-not-allowed min-h-11 sm:min-h-0"
            title="Coming soon"
          >
            Blocks
          </button>
          <button
            disabled
            className="px-4 py-2 text-sm font-medium text-fg-muted opacity-50 cursor-not-allowed min-h-11 sm:min-h-0"
            title="Coming soon"
          >
            Buildings
          </button>
        </div>
      </div>

      {/* Privacy banner */}
      <div className="bg-status-info-bg/20 border border-status-info-bg rounded-lg p-4 flex gap-3 items-start">
        <div className="text-status-info text-lg flex-shrink-0">ℹ</div>
        <p className="text-sm text-status-info">
          {!privacyError
            ? `Rows with fewer than ${kAnonFloor} voters are hidden to protect individual privacy.`
            : 'Rows with fewer than 5 voters are hidden to protect individual privacy.'}
        </p>
      </div>

      {/* Main filter bar */}
      <FilterBar
        activeFilters={activeFilterChips}
        onClearAll={query.clearAll}
      >
        <SearchInput
          value={query.search}
          onChange={handleSearchChange}
          placeholder="Search street name"
        />

        <FilterSelect
          label="Precinct"
          value={currentPrecinct}
          options={precinctOptions}
          onChange={handlePrecinctChange}
          placeholder="All precincts"
        />

        <FilterSelect
          label="Subregion"
          value={currentSubregion}
          options={subregionOptions}
          onChange={handleSubregionChange}
          placeholder="All subregions"
        />

        <FilterRange
          label="Minimum avg score"
          min={0}
          max={100}
          value={[minScore, null]}
          onChange={handleMinScoreChange}
        />
      </FilterBar>

      {/* Data table */}
      <DataTable<StreetRollupRow>
        columns={columns}
        query={query}
        rowKey={(row: any) => `${row.street_name}-${row.city}-${row.precinct_num ?? ''}`}
        emptyMessage="No streets in this campaign."
        emptyFilteredMessage="No streets match these filters."
        mobileMode="cards"
      />
    </div>
  );
}

export default PowerMapView;

// ============================================================================
// Sub-components
// ============================================================================

function ScoreBadge({ score }: { score: number }) {
  let bgColor = 'bg-bg-elevated/50';
  let textColor = 'text-fg-secondary';

  if (score >= 85) {
    bgColor = 'bg-status-danger-bg/30';
    textColor = 'text-status-danger';
  } else if (score >= 70) {
    bgColor = 'bg-status-warning-bg/30';
    textColor = 'text-status-warning';
  } else if (score >= 55) {
    bgColor = 'bg-status-warning-bg/30';
    textColor = 'text-status-warning';
  }

  return (
    <span className={`inline-block px-2 py-1 rounded text-[11px] font-semibold ${bgColor} ${textColor}`}>
      {score.toFixed(1)}
    </span>
  );
}
